/**
 * CredentialFields Component
 *
 * Renders dynamic input fields for external authentication credentials based on
 * product configuration. Displays configured labels, placeholders, documentation
 * links, and test credentials notes.
 *
 * Requirements:
 * - 2.1: Render input fields for all fields in `external_auth_config.fields`
 * - 2.2: Display configured labels from `field_labels` or use field name as fallback
 * - 2.3: Display configured placeholders from `field_placeholders`
 * - 2.4: Display documentation link if `documentation_url` is provided
 * - 2.5: Display test credentials note if `test_credentials_available` is true
 * - 2.6: Use field name as label when no label is configured
 * - 2.7: Render fields in order they appear in `fields` array
 * - 3.5: Display validation errors for missing fields
 */

import { ExternalAuthConfig, ExternalCredentials } from '../types';

export interface CredentialFieldsProps {
  /** External authentication configuration from product */
  config: ExternalAuthConfig;
  /** Current credential values */
  values: ExternalCredentials;
  /** Callback when credential values change */
  onChange: (values: ExternalCredentials) => void;
  /** Validation errors (list of missing field names) */
  validationErrors?: string[];
}

/**
 * CredentialFields component renders dynamic input fields for external authentication.
 *
 * @param config - External authentication configuration
 * @param values - Current credential values
 * @param onChange - Callback function when values change
 * @param validationErrors - Optional list of missing field names for validation display
 */
export const CredentialFields = ({
  config,
  values,
  onChange,
  validationErrors = [],
}: CredentialFieldsProps) => {
  /**
   * Handle input change for a specific credential field
   */
  const handleChange = (fieldName: string, value: string) => {
    onChange({
      ...values,
      [fieldName]: value,
    });
  };

  /**
   * Get the display label for a field
   * Uses configured label from field_labels or falls back to field name
   */
  const getFieldLabel = (fieldName: string): string => {
    return config.field_labels?.[fieldName] || fieldName;
  };

  /**
   * Get the placeholder text for a field
   * Uses configured placeholder from field_placeholders or returns undefined
   */
  const getFieldPlaceholder = (fieldName: string): string | undefined => {
    return config.field_placeholders?.[fieldName];
  };

  /**
   * Check if a field has a validation error
   */
  const hasError = (fieldName: string): boolean => {
    return validationErrors.includes(fieldName);
  };

  /**
   * Determine input type based on field name
   * Password fields should use type="password" for security
   */
  const getInputType = (fieldName: string): string => {
    const lowerFieldName = fieldName.toLowerCase();
    if (lowerFieldName.includes('password') || lowerFieldName.includes('secret')) {
      return 'password';
    }
    return 'text';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">External Authentication Credentials</h3>

        {/* Documentation link (Requirement 2.4) */}
        {config.documentation_url && (
          <a
            href={config.documentation_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-800 underline"
          >
            View Documentation
          </a>
        )}
      </div>

      {/* Authentication description */}
      {config.description && <p className="text-sm text-gray-600">{config.description}</p>}

      {/* Test credentials note (Requirement 2.5) */}
      {config.test_credentials_available && config.test_credentials_note && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <p className="text-sm text-blue-800">
            <span className="font-medium">Test Credentials Available:</span>{' '}
            {config.test_credentials_note}
          </p>
        </div>
      )}

      {/* Validation errors display (Requirement 3.5) */}
      {validationErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-800">
            <span className="font-medium">Missing required fields:</span>{' '}
            {validationErrors.join(', ')}
          </p>
        </div>
      )}

      {/* Credential input fields (Requirements 2.1, 2.7) */}
      <div className="space-y-3">
        {config.fields.map((fieldName) => (
          <div key={fieldName} className="space-y-1">
            <label
              htmlFor={`credential-${fieldName}`}
              className="block text-sm font-medium text-gray-700"
            >
              {/* Display label (Requirements 2.2, 2.6) */}
              {getFieldLabel(fieldName)}
              <span className="text-red-500 ml-1" aria-label="required">
                *
              </span>
            </label>

            <input
              id={`credential-${fieldName}`}
              type={getInputType(fieldName)}
              value={values[fieldName] || ''}
              onChange={(e) => handleChange(fieldName, e.target.value)}
              required
              placeholder={getFieldPlaceholder(fieldName)} // Requirement 2.3
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                hasError(fieldName) ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              aria-invalid={hasError(fieldName)}
              aria-describedby={hasError(fieldName) ? `${fieldName}-error` : undefined}
            />

            {/* Field-specific error message */}
            {hasError(fieldName) && (
              <p id={`${fieldName}-error`} className="text-sm text-red-600 mt-1">
                This field is required
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CredentialFields;
